import React, { useState, useEffect, useRef } from 'react';
import './style.less';

const TECH_TABS = [
  {
    id: 'ai',
    index: '01',
    label: '原生 AI 智能体与模型网关',
    tag: 'AI 辅助研发架构',
    title: '主容器原生双智能体 + 企业级统一模型网关',
    subtitle:
      '告别边车容器工具链割裂与 API Key 散落泄密。Codex 与 Steerable 直接运行在工作间主容器内，通过平台网关安全调用企业内网大模型。',
    pillars: [
      {
        name: '主容器原生执行，零工具链割裂',
        detail:
          '/usr/local/bin/codex、steerable、geekcode-ai 直接在工作间主容器运行，完整共享项目内的 JDK、Maven、GCC、Python 与依赖包，可直接代跑构建与单测。',
      },
      {
        name: 'Secret 卷动态热更新（≤ 1 分钟生效）',
        detail:
          '控制台切换项目允许的模型或调整额度后，API 自动重写 ws-<实例>-ai Secret；运行中的工作间无需重建或重启，下一次启动智能体即刻生效。',
      },
      {
        name: '双协议透明适配与密钥隔离',
        detail:
          '网关 /api/v1/ai/v1/* 同时支持 OpenAI Responses 与 Chat Completions 流式协议转换；上游密钥仅存控制面，工作间只持有专属 gct_ 令牌。',
      },
    ],
  },
  {
    id: 'security',
    index: '02',
    label: '纵深安全防御与等保三级合规',
    tag: '纵深安全与合规闭环',
    title: '从身份鉴别、五层 DLP 到防篡改哈希链审计的全链路闭环',
    subtitle:
      '专为金融、政企与集团外包研发打造，满足等保三级对身份鉴别、最小权限、数据防泄漏与安全审计的严苛验收标准。',
    pillars: [
      {
        name: '等保身份鉴别与精准会话管控',
        detail:
          '内置登录失败锁定与 IP 限流、TOTP 双因子认证、首次强制改密、密码有效期策略，支持 OIDC / CAS 统一认证，并对空闲超时与绝对过期提供明确退出提示。',
      },
      {
        name: '五层 DLP 策略合成与 SSH 零私钥托管',
        detail:
          '平台、项目、项目角色、个人、审批例外五层合并（禁止优先）；用户在账户设置自助管理 SSH 公钥，平台不存私钥，撤权或删钥后 60 秒内强制断开活跃会话。',
      },
      {
        name: 'SHA-256 哈希链防篡改审计',
        detail:
          '全量记录登录、工作间开关、Web IDE 上传下载、SFTP/SCP 传输与端口转发；采用只追加授权与哈希链防篡改校验，180 天留存，百万行查询 p95 ≤ 2 秒，支持 syslog 外送。',
      },
    ],
  },
  {
    id: 'images',
    index: '03',
    label: '五层标准镜像体系与秒级预热',
    tag: '标准镜像与极速调度',
    title: '五层可复现镜像架构 + 自动化安全合规门禁 + 节点预热',
    subtitle:
      '统一 Ubuntu 24.04 底座消除上游冗余 CVE 漏洞，打通镜像构建、离线漏洞扫描、安全准入门禁与 DaemonSet 节点预热全生命周期。',
    pillars: [
      {
        name: '五层同源可复现构建（L1–L5）',
        detail:
          '从 L1 enterprise-base（Ubuntu 24.04 + sshd + DLP + UID 1000）派生 L2 网页 IDE（VS Code / JupyterLab 4 / IDEA）、L3 语言栈、L4 智能体变体与 L5 厂商叠加层。',
      },
      {
        name: '16 项安全准入校验与离线漏洞门禁',
        detail:
          '入库自动校验非 root 用户、标准入口脚本、DLP 代理完整性与高危 SUID 清单，配合离线漏洞库扫描，存在阻断级风险自动拦截发布。',
      },
      {
        name: 'DaemonSet 多集群自动预热',
        detail:
          '镜像版本发布后自动在所有 ready 工作集群下发预热任务，新节点加入自动拉取，开发者点击创建工作间即可秒级进入编码状态。',
      },
    ],
  },
];

const TechShowcase = () => {
  const [activeTab, setActiveTab] = useState('ai');
  const tabRefs = useRef([]);

  useEffect(() => {
    const handleExternalSwitch = (e) => {
      const targetTab = e?.detail?.tab;
      if (targetTab && TECH_TABS.some((t) => t.id === targetTab)) {
        setActiveTab(targetTab);
      }
    };
    window.addEventListener('geekcode:switch-tech-tab', handleExternalSwitch);
    return () => window.removeEventListener('geekcode:switch-tech-tab', handleExternalSwitch);
  }, []);

  const handleKeyDown = (e, idx) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (idx + 1) % TECH_TABS.length;
      setActiveTab(TECH_TABS[next].id);
      tabRefs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (idx - 1 + TECH_TABS.length) % TECH_TABS.length;
      setActiveTab(TECH_TABS[prev].id);
      tabRefs.current[prev]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActiveTab(TECH_TABS[0].id);
      tabRefs.current[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = TECH_TABS.length - 1;
      setActiveTab(TECH_TABS[last].id);
      tabRefs.current[last]?.focus();
    }
  };

  const current = TECH_TABS.find((t) => t.id === activeTab) || TECH_TABS[0];

  return (
    <div className="tech-showcase">
      <div id="ai-agents" className="anchor-sentinel" aria-hidden="true" />
      <div id="security" className="anchor-sentinel" aria-hidden="true" />

      <div className="tech-tabs-bar" role="tablist" aria-label="核心技术架构模块">
        {TECH_TABS.map((tab, idx) => {
          const isSelected = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              ref={(el) => {
                tabRefs.current[idx] = el;
              }}
              type="button"
              role="tab"
              id={`tech-tab-${tab.id}`}
              aria-selected={isSelected}
              aria-controls={`tech-panel-${tab.id}`}
              tabIndex={isSelected ? 0 : -1}
              className={`tech-tab-btn ${isSelected ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
            >
              <span className="tab-num">{tab.index}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`tech-panel-${current.id}`}
        aria-labelledby={`tech-tab-${current.id}`}
        className="tech-panel cloud-collab-card"
      >
        <div className="panel-left">
          <span className="panel-tag">{current.tag}</span>
          <h3>{current.title}</h3>
          <p className="panel-subtitle">{current.subtitle}</p>

          <div className="pillars-list">
            {current.pillars.map((pillar, i) => (
              <div key={pillar.name} className="pillar-item">
                <div className="pillar-index">0{i + 1}</div>
                <div className="pillar-body">
                  <h4>{pillar.name}</h4>
                  <p>{pillar.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel-right">
          {current.id === 'ai' && (
            <div className="arch-diagram ai-diagram">
              <div className="diagram-title">端到端 AI 模型配置下发与网关调用链路</div>
              <div className="flow-step">
                <div className="step-head">
                  <span className="step-badge">STEP 1 · 平台与项目控制台</span>
                  <span className="step-ok">三项连通性检测通过</span>
                </div>
                <div className="step-desc">
                  登记内网模型服务（DeepSeek / Qwen / vLLM），验证 <code>连通</code> · <code>流式输出</code> · <code>工具调用</code>，按项目勾选允许模型与每日额度。
                </div>
              </div>
              <div className="flow-arrow" aria-hidden="true">▼ Secret 卷自动挂载（≤1 分钟同步，无需重启容器）</div>
              <div className="flow-step">
                <div className="step-head">
                  <span className="step-badge">STEP 2 · 工作间主容器 (/etc/geekcode/ai/)</span>
                  <span className="step-ok">共享容器内工具链</span>
                </div>
                <div className="step-code">
                  <code>token: gct_*** (工作间专属令牌，不含上游密钥)</code>
                  <code>agent.json: 网关地址、允许模型列表、默认模型</code>
                  <code>CLI: /usr/local/bin/codex · steerable · geekcode-ai</code>
                </div>
              </div>
              <div className="flow-arrow" aria-hidden="true">▼ Authorization: Bearer gct_***</div>
              <div className="flow-step">
                <div className="step-head">
                  <span className="step-badge">STEP 3 · 平台 AI 模型网关 (/api/v1/ai/v1/*)</span>
                  <span className="step-ok">Responses ⇄ Chat 协议转换</span>
                </div>
                <div className="step-desc">
                  校验工作间令牌、项目授权模型与剩余额度 ──&gt; 注入控制面托管的上游 API Key ──&gt; 实时记录 Token 消耗与提问摘要审计。
                </div>
              </div>
            </div>
          )}

          {current.id === 'security' && (
            <div className="arch-diagram security-diagram">
              <div className="diagram-title">企业级四层纵深安全防御矩阵</div>
              <div className="sec-layer">
                <div className="layer-header">
                  <span className="layer-name">01 · 等保身份鉴别与会话闭环</span>
                  <span className="layer-tag">多因素与统一认证</span>
                </div>
                <div className="layer-chips">
                  <span>失败锁定 + IP 限流</span>
                  <span>TOTP 动态口令</span>
                  <span>首次登录强制改密</span>
                  <span>OIDC / CAS 统一认证</span>
                  <span>超时退出精准提示</span>
                </div>
              </div>
              <div className="sec-layer">
                <div className="layer-header">
                  <span className="layer-name">02 · 五层 DLP 策略合成（禁止优先）</span>
                  <span className="layer-tag">五层合并计算</span>
                </div>
                <div className="layer-formula">
                  <code>平台策略 ∩ 项目策略 ∩ 项目角色 ∩ 个人策略 ∩ 审批例外 ──&gt; 生效策略</code>
                </div>
                <div className="layer-chips">
                  <span>禁止文件下载</span>
                  <span>拦截剪贴板复制/剪切</span>
                  <span>禁用远程接入 (60s 断连)</span>
                  <span>斜向防删改动态水印</span>
                </div>
              </div>
              <div className="sec-layer">
                <div className="layer-header">
                  <span className="layer-name">03 · SSH 网关与公钥生命周期</span>
                  <span className="layer-tag">零私钥托管</span>
                </div>
                <div className="layer-chips">
                  <span>账户自助添加公钥</span>
                  <span>平台零私钥生成/存储</span>
                  <span>吊销公钥 60s 断开会话</span>
                  <span>DLP 禁用时自动拒连</span>
                </div>
              </div>
              <div className="sec-layer">
                <div className="layer-header">
                  <span className="layer-name">04 · 防篡改安全审计</span>
                  <span className="layer-tag">180 天留存</span>
                </div>
                <div className="layer-chips">
                  <span>SHA-256 哈希链防篡改</span>
                  <span>数据库只追加授权</span>
                  <span>100 万行 p95 ≤ 2s</span>
                  <span>Syslog 实时外送</span>
                </div>
              </div>
            </div>
          )}

          {current.id === 'images' && (
            <div className="arch-diagram image-diagram">
              <div className="diagram-title">官方工作间镜像五层架构（L1–L5）与发布流水线</div>
              <div className="stack-layers">
                <div className="stack-row l5">
                  <span className="lv">L5 厂商叠加层</span>
                  <span className="ldesc">企业专属插件与内网配置叠加（按交付档案出包构建）</span>
                </div>
                <div className="stack-row l4">
                  <span className="lv">L4 智能体变体</span>
                  <span className="ldesc">默认预装 Codex + Steerable 命令行 │ -noagent 纯净变体</span>
                </div>
                <div className="stack-row l3">
                  <span className="lv">L3 语言工具栈</span>
                  <span className="ldesc">java (JDK 17+21) · go · node · cpp · datasci (同源构建)</span>
                </div>
                <div className="stack-row l2">
                  <span className="lv">L2 网页 IDE 层</span>
                  <span className="ldesc">code-server (VS Code) · notebook (JupyterLab 4) · IDEA</span>
                </div>
                <div className="stack-row l1">
                  <span className="lv">L1 平台底座层</span>
                  <span className="ldesc">enterprise-base: Ubuntu 24.04 + sshd + DLP 代理 + UID 1000 (/home/workspace)</span>
                </div>
              </div>
              <div className="pipeline-footer">
                <span>① 批次锁文件签名</span>
                <span aria-hidden="true">→</span>
                <span>② 16 项安全合规校验</span>
                <span aria-hidden="true">→</span>
                <span>③ 离线 CVE 扫描门禁</span>
                <span aria-hidden="true">→</span>
                <span>④ DaemonSet 节点预热</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechShowcase;
